import mapboxgl from "mapbox-gl";

import { doesMapboxLayerMatchOrderedLayer } from "components/map/hooks/manage-mapbox-layer-sorting/util";
import { IEditableProjectLayer } from "components/project/layers/hooks";

interface IGetBeforeIdForNewShapefileLayersParams {
  mapbox: mapboxgl.Map;
  layerList: IEditableProjectLayer[];
  layerName: string;
}

/**
 * Finds the mapbox layer id a newly (re-)added shapefile's layers should be inserted
 * beneath, so they land at their saved stacking position right away instead of on
 * top of everything until the next layer-sort pass runs.
 *
 * The id is taken from the live mapbox style (an id mapbox can't resolve makes
 * addLayer fail outright): the bottom-most live layer belonging to an entry checked
 * above this shapefile in the layer list (order 0 = top of the list = rendered on
 * top). The shapefile's own leftover variant layers are skipped mid-rebuild. Returns
 * null when nothing above it is on the map yet, which appends on top and lets the
 * sorter finish the job.
 */
export function getBeforeIdForNewShapefileLayers(
  params: IGetBeforeIdForNewShapefileLayersParams
): Maybe<string> {
  const { mapbox, layerList, layerName } = params;

  const mapboxLayers = mapbox.getStyle()?.layers;
  const ownLayer = layerList.find(
    (layer) =>
      layer.isChecked && (layer.projectShapefileId || layer.shapefileId) === layerName
  );
  if (!mapboxLayers || !ownLayer) {
    return null;
  }

  const layersAbove = layerList.filter(
    (layer) => layer.isChecked && layer.order < ownLayer.order
  );

  const anchor = mapboxLayers.find(
    (layer) =>
      !doesMapboxLayerMatchOrderedLayer(layer, ownLayer) &&
      layersAbove.some((aboveLayer) =>
        doesMapboxLayerMatchOrderedLayer(layer, aboveLayer)
      )
  );

  return anchor?.id ?? null;
}
